/* eslint-disable react-refresh/only-export-components */
import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  type CognitoUserSession,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import axios from "axios";
import React, {
  createContext,
  useEffect,
  useReducer,
  useMemo,
  useCallback,
  type ReactNode,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";

import SplashScreen from "../components/SplashScreen";
import { AWS_API_NEW } from "../config";
import accountReducer from "../store/accountReducer";
import { LOGIN, LOGOUT, NEW_PASSWORD } from "../store/actions";

import type { AccountState, User } from "../store/accountReducer";

const baseURL = import.meta.env.VITE_API_BASE_URL;
const basePath = import.meta.env.VITE_API_BASE_PATH;
// Types
export interface AWSCognitoContextType {
  isAuthenticated: boolean;
  user: User | null | undefined;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (
    email: string,
    code: string,
    newPassword: string
  ) => Promise<void>;
  isNewPassword: boolean;
  userAttributes: any;
  changePassword: (
    newPassword: string,
    userAttributes: any,
    cognitoUser: CognitoUser | null
  ) => Promise<void>;
  cognitoUser?: CognitoUser | null;
}
interface AWSCognitoProviderProps {
  children: ReactNode;
}
interface UserData {
  name: string | null;
  email: string;
  position: {
    value: string | null;
  } | null;
  person_id?: string | null;
  company_id?: string | null;
}
// Initial State
const initialState: AccountState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  isNewPassword: false,
  userAttributes: null,
  cognitoUser: null,
};
let userPool: CognitoUserPool | null = null;
try {
  if (!AWS_API_NEW.UserPoolId || !AWS_API_NEW.ClientId) {
    throw new Error(
      "Both UserPoolId and ClientId are required to initialize Cognito User Pool."
    );
  }
  userPool = new CognitoUserPool({
    UserPoolId: AWS_API_NEW.UserPoolId,
    ClientId: AWS_API_NEW.ClientId,
  });
} catch (error) {
  console.error("Cognito Initialization Error:", error);
}

const setSession = (
  tokens: { idToken: string; refreshToken: string } | null
): void => {
  if (tokens) {
    localStorage.setItem("tmaWebAppServiceToken", tokens.idToken);
    localStorage.setItem("tmaWebAppRefreshToken", tokens.refreshToken);
  } else {
    localStorage.removeItem("tmaWebAppServiceToken");
    localStorage.removeItem("tmaWebAppRefreshToken");
  }
};

export const refreshIdToken = async (): Promise<string | null> => {
  if (!userPool) {
    return Promise.reject(new Error("Cognito User Pool is not initialized."));
  }

  return new Promise((resolve, reject) => {
    // Retrieve the current CognitoUser
    const user = userPool.getCurrentUser();
    if (!user) {
      return reject(new Error("No current user found."));
    }
    const refreshToken = localStorage.getItem("tmaWebAppRefreshToken");
    if (!refreshToken) {
      return reject(new Error("No refresh token found."));
    }
    // Create a CognitoRefreshToken object
    const refreshTokenObj = new CognitoRefreshToken({
      RefreshToken: refreshToken,
    });
    // Use the refresh token to get a new session
    return user.refreshSession(
      refreshTokenObj,
      (err: any, session: CognitoUserSession) => {
        if (err) {
          return reject(err);
        }
        const newIdToken = session.getIdToken().getJwtToken();
        const newRefreshToken = session.getRefreshToken().getToken();
        // Optionally, update stored tokens
        setSession({ idToken: newIdToken, refreshToken: newRefreshToken });
        return resolve(newIdToken);
      }
    );
  });
};
// Context
const AWSCognitoContext = createContext<AWSCognitoContextType | null>(null);
export const AWSCognitoProvider: React.FC<AWSCognitoProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const logout = useCallback((): void => {
    if (!userPool) {
      console.error("Cognito User Pool is not initialized.");
      return;
    }
    const loggedInUser = userPool.getCurrentUser();
    if (loggedInUser) {
      loggedInUser.signOut();
    }
    setSession(null);
    dispatch({ type: LOGOUT });
    navigateRef.current("/login", { replace: true });
  }, []);

  const fetchUserDetails = useCallback(
    async (email: string): Promise<UserData | undefined | null> => {
      try {
        const user = await axios.get(`${baseURL}/${basePath}/people`, {
          params: {
            email,
          },
          headers: {
            Authorization: localStorage.getItem("tmaWebAppServiceToken") || "",
          },
        });
        return user?.data?.data[0];
      } catch {
        throw new Error("Failed to fetch user details.");
      }
    },
    []
  );
  useEffect(() => {
    const init = async () => {
      try {
        const refreshToken = localStorage.getItem("tmaWebAppRefreshToken");
        if (refreshToken && userPool) {
          const user = userPool.getCurrentUser();
          if (user) {
            const refreshTokenObj = new CognitoRefreshToken({
              RefreshToken: refreshToken,
            });
            user.refreshSession(
              refreshTokenObj,
              async (err: any, session: CognitoUserSession) => {
                if (err) {
                  logout();
                  setLoading(false);
                  return;
                }
                const newIdToken = session.getIdToken().getJwtToken();
                const newRefreshToken = session.getRefreshToken().getToken();
                setSession({
                  idToken: newIdToken,
                  refreshToken: newRefreshToken,
                });
                try {
                  const userData = await fetchUserDetails(
                    session.getIdToken().payload.email
                  );
                  console.log("Fetched user data during initialization:", userData);
                  sessionStorage.setItem("UserId", userData?.person_id || "");
                  const userDetail = {
                    id: session.getIdToken().payload.sub,
                    name: userData?.name || null,
                    email: session.getIdToken().payload.email,
                    role: userData?.position?.value || null,
                    token: newIdToken,
                    person_id: userData?.person_id,
                    company_id: userData?.company_id,
                  };
                  dispatch({
                    type: LOGIN,
                    user: userDetail,
                    isLoggedIn: true,
                    token: newIdToken,
                  });
                } catch {
                  logout();
                } finally {
                  setLoading(false);
                }
              }
            );
          } else {
            logout();
            setLoading(false);
          }
        } else {
          dispatch({ type: LOGOUT });
          setLoading(false);
        }
      } catch (error) {
        console.error("Initialization Error:", error);
        dispatch({ type: LOGOUT });
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      if (!userPool) {
        throw new Error("Cognito User Pool is not initialized.");
      }
      return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: email, Pool: userPool });
        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });
        user.authenticateUser(authDetails, {
          newPasswordRequired: (userAttributes) => {
            delete userAttributes.email_verified;
            dispatch({
              type: NEW_PASSWORD,
              userAttributes,
              cognitoUser: user,
            });

            resolve(); // Resolve to allow UI to transition to new password screen
          },
          onSuccess: async (session: CognitoUserSession) => {
            const idToken = session.getIdToken().getJwtToken();
            const refreshToken = session.getRefreshToken().getToken();
            setSession({ idToken, refreshToken });
            try {
              const userData = await fetchUserDetails(
                session.getIdToken().payload.email
              );
              if (userData) {
                sessionStorage.setItem("UserId", userData.person_id || "");
              }
              const userDetail = {
                id: session.getIdToken().payload.sub,
                name: userData?.name || null,
                email: session.getIdToken().payload.email,
                role: userData?.position?.value || null,
                token: idToken,
                person_id: userData?.person_id,
                company_id: userData?.company_id,
              };
              dispatch({
                type: LOGIN,
                user: userDetail,
                isLoggedIn: true,
                token: userDetail.token, // optional but matches type
              });

              navigateRef.current("/voyages", { replace: true });
              resolve();
            } catch (error) {
              setSession(null);
              reject(error);
            }
          },
          onFailure: (err: any) => {
            reject(err);
          },
        });
        
      });
    },
    [fetchUserDetails]
  );
  useEffect(() => {
    const handleLogoutEvent = () => {
      logout();
    };
    window.addEventListener("auth:logout", handleLogoutEvent);
    return () => {
      window.removeEventListener("auth:logout", handleLogoutEvent);
    };
  }, [logout]);
  const forgotPassword = useCallback((email: string): Promise<void> => {
    if (!userPool) {
      return Promise.reject(new Error("Cognito User Pool is not initialized."));
    }
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: email,
        Pool: userPool,
      });
      user.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err: any) => {
          reject(err);
        },
      });
    });
  }, []);
  const resetPassword = useCallback(
    (email: string, code: string, newPassword: string): Promise<void> => {
      if (!userPool) {
        return Promise.reject(
          new Error("Cognito User Pool is not initialized.")
        );
      }
      return new Promise((resolve, reject) => {
        const user = new CognitoUser({
          Username: email,
          Pool: userPool,
        });
        user.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (err: any) => {
            reject(err);
          },
        });
      });
    },
    []
  );
  const changePassword = useCallback(
    (
      newPassword: string,
      userAttributes: any,
      cognitoUser: CognitoUser | null
    ): Promise<void> => {
      if (!cognitoUser) {
        return Promise.reject(new Error("Cognito user not found"));
      }
      return new Promise((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, null, {
          onFailure(err) {
            reject(err);
          },
          onSuccess() {
            resolve();
          },
        });
      });
    },
    []
  );
  const contextValue = useMemo(
    () => ({
      ...state,
      isAuthenticated: state.isLoggedIn,
      user: state.user,
      userAttributes: state.userAttributes,
      login,
      logout,
      forgotPassword,
      resetPassword,
      changePassword,
      cognitoUser: state.cognitoUser,
    }),
    [state, login, logout, resetPassword, forgotPassword, changePassword]
  );
  return (
    <AWSCognitoContext.Provider value={contextValue}>
      {loading ? <SplashScreen /> : children}
    </AWSCognitoContext.Provider>
  );
};
export default AWSCognitoContext;
